import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MapPin, Check } from "lucide-react";

export default function Onboarding() {
  const navigate = useNavigate();
  const [sportsList, setSportsList] = useState([]);
  const [cities, setCities] = useState([]);
  const [picked, setPicked] = useState([]);
  const [city, setCity] = useState("");
  const [openToJoin, setOpenToJoin] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/filters").then((r) => { setSportsList(r.data?.sports || []); setCities(r.data?.cities || []); }).catch(() => {});
    api.get("/me/athlete").then((r) => {
      if (r.data) { setPicked(r.data.sports || (r.data.sport ? [r.data.sport] : [])); setCity(r.data.city || ""); setOpenToJoin(r.data.open_to_join ?? true); }
    }).catch(() => {});
  }, []);

  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/me/athlete", { sports: picked, city, open_to_join: openToJoin });
      toast.success("Profile ready - welcome to SPORA1!");
      navigate("/app");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="grain min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-12 animate-fadeUp">
        <img src="/spora1-logo.webp" alt="SPORA1" className="mx-auto h-11 w-auto" />
        <h1 className="mt-6 text-center font-display text-3xl font-bold tracking-tight sm:text-4xl">Let's personalize your game</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">Pick your sports and city - we'll tailor your home feed, matches and grounds.</p>

        <div className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-sm">
          <label className="block text-sm font-semibold">Which sports do you play?</label>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="onboarding-sports">
            {sportsList.map((s) => {
              const on = picked.includes(s);
              return (
                <button key={s} type="button" data-testid={`onboarding-sport-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => toggle(s)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${
                    on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground hover:border-primary/40"}`}>
                  {on && <Check className="h-3.5 w-3.5" />} {s}
                </button>
              );
            })}
          </div>

          <label className="mt-6 block text-sm font-semibold">Your city</label>
          <div className="relative mt-2">
            <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input data-testid="onboarding-city" value={city} onChange={(e) => setCity(e.target.value)} list="onb-cities" placeholder="e.g. Bengaluru"
              className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary" />
            <datalist id="onb-cities">{cities.map((c) => <option key={c} value={c} />)}</datalist>
          </div>

          <label className="mt-6 flex items-center justify-between rounded-lg border border-border bg-background p-3">
            <span className="text-sm"><span className="font-semibold">Open to join teams</span><br /><span className="text-xs text-muted-foreground">Let captains discover and invite you.</span></span>
            <button type="button" data-testid="onboarding-open-toggle" onClick={() => setOpenToJoin((v) => !v)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${openToJoin ? "bg-primary" : "bg-muted"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${openToJoin ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </label>

          <button data-testid="onboarding-save" onClick={save} disabled={loading || picked.length === 0}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 font-semibold text-primary-foreground transition-all hover:bg-[#FF8533] disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Continue
          </button>
          <button data-testid="onboarding-skip" onClick={() => navigate("/app")} className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-foreground">Skip for now</button>
        </div>
      </div>
    </div>
  );
}
